import React from 'react';

import {
  Building,
  EyeShut,
  Globe,
  LinkSecurityLock,
  Lock,
  LockPassword,
  LockAccess,
} from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

import {
  UpsellType,
  PrivacyOptionType,
  PrivacyType,
  EventPrivacyType,
  EmbedPrivacyType,
  DeprecatedPrivacyType,
} from './types';

export const videoPrivacyIcons = (
  iconSize: string,
): Record<string, { icon: React.JSX.Element }> => ({
  password: { icon: <LockPassword boxSize={iconSize} /> },
  unlisted: { icon: <LinkSecurityLock boxSize={iconSize} /> },
  disable: { icon: <EyeShut boxSize={iconSize} /> },
  team: { icon: <Building boxSize={iconSize} /> },
  nobody: { icon: <Lock boxSize={iconSize} /> },
  anybody: { icon: <Globe boxSize={iconSize} /> },
  purgatory: { icon: <LockAccess boxSize={iconSize} /> },
  embed_only: { icon: <EyeShut boxSize={iconSize} /> },
});

export const VIDEO_API_VERSION = '3.4.15';

export const DEFAULT_PRIVACY_OPTIONS: PrivacyOptionType<PrivacyType>[] = [
  {
    privacy: 'unlisted',
    title: translate('Unlisted'),
    description: translate('Only people with the link can view'),
    upsellEvent: 'unlisted privacy',
  },
  {
    privacy: 'password',
    title: translate('Password'),
    description: translate('Only people with the password can view'),
    upsellEvent: 'password privacy',
  },
  {
    privacy: 'disable',
    title: translate('Hide from Vimeo'),
    description: translate('Private on your account, but embeddable anywhere'),
    upsellEvent: 'disable privacy',
  },
  {
    privacy: 'team',
    title: translate('Team'),
    description: translate('Anyone in this team can view'),
  },
  {
    privacy: 'nobody',
    title: translate('Private'),
    description: translate('Only you and people with access can view'),
  },
  {
    privacy: 'anybody',
    title: translate('Public'),
    description: translate('Anyone on the internet can view'),
  },
];

export const DEFAULT_PRIVACY_VALUES: Record<string, PrivacyType> = {
  UNLISTED: 'unlisted',
  PASSWORD: 'password',
  HIDE_FROM_VIMEO: 'disable',
  TEAM: 'team',
  PRIVATE: 'nobody',
  PUBLIC: 'anybody',
};

export const DEPRECATED_PRIVACIES: DeprecatedPrivacyType[] = [
  'ptv',
  'ptvhide',
  'stock',
  'stock_purchased',
  'users',
  'contacts',
];

export const EVENT_PRIVACY_VALUES: Record<string, EventPrivacyType> = {
  UNLISTED: 'unlisted',
  PASSWORD: 'password',
  HIDE_FROM_VIMEO: 'embed_only',
  TEAM: 'team',
  PRIVATE: 'nobody',
  PUBLIC: 'anybody',
};

export const EMBED_PRIVACY_VALUES: Record<string, EmbedPrivacyType> = {
  PRIVATE: 'private',
  PUBLIC: 'public',
  WHITELIST: 'whitelist',
};

export const DEFAULT_UPSELL_PRIVACIES = ['password', 'unlisted', 'disable'];

export const UPSELL_TYPE_TO_UPSALE_NAME = {
  'password privacy': 'privacy_settings_password',
  'unlisted privacy': 'privacy_settings_unlisted',
  'disable privacy': 'privacy_settings_hide_from_vimeo',
} as const;

export const VIDEO_PRIVACY_ICON_MAP = videoPrivacyIcons('xs');

export const PRIVACY_VALUE_TO_UPSELL: Partial<Record<string, UpsellType>> = {
  password: 'password privacy',
  unlisted: 'unlisted privacy',
  disable: 'disable privacy',
};

export const PAID_PRIVACY_UPSELL_MODAL_VALUES = new Set(['password', 'unlisted', 'disable']);
